// Состояние актуализации цен: конфиг фичи, последняя проверка по каждому
// рейсу и признак «проверяем прямо сейчас».
//
// Провайдер стоит ВНУТРИ StoreProvider, AuthProvider и I18nProvider: ему нужны
// справочник валют поездки, сессия и переводы. Гость (401) сидит тихо —
// ready:false и ни одной ошибки на экране: так же ведут себя ИИ-фичи.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { FlightSegment, Segment } from '../types';
import { useAuth } from '../auth';
import type { ApiError } from '../auth';
import { useT } from '../i18n';
import type { Translate } from '../i18n';
import { useStore } from '../store';
import { applyPriceQuote, checkPrices, fetchPriceChecks, fetchPriceConfig } from './api';
import { availability, type ConfigProbe } from './availability';
import { limitFlights } from './staleness';
import { isFullConfig } from './types';
import type { PriceCheck, PriceConfig, PriceFlightInput, PriceQuote, PricesApi } from './types';

export interface PricesProviderProps {
  tripId: string | null;
  children: ReactNode;
}

/** Коды сервера, у которых есть свой текст; остальное — prices.err.unknown. */
const KNOWN_ERROR_CODES = new Set([
  'disabled',
  'no_key',
  'rate_limited',
  'quota',
  'provider',
  'bad_request',
]);

/**
 * `unauthorized` наружу не показываем вовсе: это не поломка, а отсутствие
 * входа, и отвечает на него ready:false, а не красная строка. Всё незнакомое
 * (в том числе `network` при недоступном сервере и `forbidden`) честнее
 * назвать неизвестной ошибкой, чем выдать за ответ провайдера.
 */
function errorMessage(t: Translate, err: ApiError): string | null {
  if (err.error === 'unauthorized') return null;
  const code = KNOWN_ERROR_CODES.has(err.error) ? err.error : 'unknown';
  return t(`prices.err.${code}`);
}

/** Рейс → то, что уходит на проверку. Пустая строка вместо отсутствующего. */
function toFlightInput(seg: FlightSegment, library: Record<string, Segment>): PriceFlightInput {
  // Дата вылета обратного рейса помогает агрегатору найти ту же цену, что
  // видел человек: у пары туда-обратно цена одного плеча в отрыве не ищется.
  const partner = seg.linkedTo ? library[seg.linkedTo] : undefined;
  return {
    segmentId: seg.id,
    from: seg.from,
    to: seg.to,
    departLocal: seg.departLocal,
    arriveLocal: seg.arriveLocal,
    carrier: seg.carrier,
    flightNumber: seg.flightNumber ?? '',
    priceMinor: seg.price.amount,
    currency: seg.price.currency,
    priceScope: seg.priceScope,
    // Ссылка у рейса необязательная, и ничто в проверке от неё не зависит.
    url: seg.url ?? '',
    linkedDepartLocal: partner && partner.kind === 'flight' ? partner.departLocal : '',
  };
}

function byId(checks: readonly PriceCheck[]): Record<string, PriceCheck> {
  const map: Record<string, PriceCheck> = {};
  for (const c of checks) map[c.segmentId] = c;
  return map;
}

const PricesContext = createContext<PricesApi | undefined>(undefined);

/**
 * Адрес поездки — внутренняя кухня модуля: за историей одного рейса модал
 * ходит сам, а тащить `tripId` в публичный `PricesApi` только ради этого
 * значит выставить наружу то, что зовущий и так передал провайдеру.
 */
const TripIdContext = createContext<string | null>(null);

export function usePricesTripId(): string | null {
  return useContext(TripIdContext);
}

export function PricesProvider({ tripId, children }: PricesProviderProps): ReactElement {
  const t = useT();
  const { status, isGuest } = useAuth();
  const state = useStore();

  const [config, setConfig] = useState<PriceConfig | null>(null);
  const [probe, setProbe] = useState<ConfigProbe>('loading');
  const [checks, setChecks] = useState<Record<string, PriceCheck>>({});
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [error, setError] = useState<string | null>(null);
  // Кто затеял неудавшееся действие. Без этого сообщение показала бы каждая
  // смонтированная кнопка — ошибка-то в контексте, а кнопок на доске десятки.
  const [errorOwner, setErrorOwner] = useState<string | null>(null);

  // Состояние и переводчик читаются в момент запроса, а не в момент создания
  // колбэка: иначе refresh пересоздавался бы на каждое движение в поездке и
  // тянул за собой перерисовку всех кнопок.
  const stateRef = useRef(state);
  stateRef.current = state;
  const tRef = useRef(t);
  tRef.current = t;
  const busyRef = useRef<ReadonlySet<string>>(busyIds);
  busyRef.current = busyIds;

  /**
   * Сообщение о неудаче вместе с тем, кому его показывать. Хозяин и сообщение
   * ставятся вместе и снимаются вместе: разъехавшись, они дадут либо немую
   * ошибку, либо чужую всплывашку у случайной кнопки.
   */
  const report = useCallback((message: string | null, owner?: string): void => {
    setError(message);
    setErrorOwner(message === null ? null : (owner ?? null));
  }, []);

  /**
   * Тихо перечитать конфиг. Неудача ничего не меняет: старые цифры лучше
   * пустоты, а мигать кнопкой из-за сетевой икоты нельзя — поэтому `probe`
   * здесь трогается только в сторону успеха.
   */
  const refreshConfig = useCallback(async (): Promise<void> => {
    const res = await fetchPriceConfig();
    if (!res.ok) return;
    setConfig(res.value);
    setProbe('ok');
  }, []);

  // Конфиг тянется один раз и заново после входа/выхода: дневной лимит зависит
  // от аккаунта. Гостя спрашиваем на равных — ему сервер отвечает 200 с одним
  // полем `enabled`, и этого ровно хватает, чтобы решить, показывать ли кнопку.
  useEffect(() => {
    if (status === 'loading') return;
    const ctrl = new AbortController();
    setProbe('loading');
    void fetchPriceConfig(ctrl.signal).then((res) => {
      if (ctrl.signal.aborted) return;
      if (res.ok) {
        setConfig(res.value);
        setProbe('ok');
        return;
      }
      // Неудача конфига — это молчание, а не ошибка на экране: фича просто
      // не появляется. Кричать о ней там, где человек её и не звал, незачем.
      setConfig(null);
      setProbe('failed');
    });
    return () => ctrl.abort();
  }, [status, isGuest]);

  const { visible, ready } = availability(probe, config?.enabled === true, isGuest);

  // Проверки — свои у каждой поездки: список соседней был бы набором чужих
  // цен, которые нечем отличить друг от друга.
  useEffect(() => {
    if (!ready) {
      setChecks((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    const ctrl = new AbortController();
    void fetchPriceChecks(tripId, ctrl.signal).then((res) => {
      if (ctrl.signal.aborted) return;
      setChecks(res.ok ? byId(res.value.checks) : {});
    });
    return () => ctrl.abort();
  }, [ready, tripId]);

  // Незавершённая проверка при уходе со страницы или смене поездки — висящий
  // запрос, ответ которого уже некуда класть.
  const runRef = useRef<AbortController | null>(null);
  useEffect(() => () => runRef.current?.abort(), []);

  const refresh = useCallback(
    async (segments: readonly Segment[], owner?: string): Promise<void> => {
      // Заслон стоит здесь, а не только на кнопке: у гостя поездка живёт
      // исключительно в его браузере, проверять на сервере попросту нечего, и
      // ни один другой вызывающий не должен уметь пробить это мимо кнопки.
      if (!ready) return;
      const flights = segments.filter(
        (s): s is FlightSegment => s.kind === 'flight' && !busyRef.current.has(s.id),
      );
      if (flights.length === 0) return;

      // Предел знает только полный ответ. Не знаем — шлём всё: выдумывать
      // число нельзя, а сервер всё равно обрежет лишнее сам, он тут главный.
      const { sent, trimmed } = limitFlights(
        flights,
        isFullConfig(config) ? config.maxFlights : undefined,
      );
      // Предупреждаем до запуска: сервер обрежет лишнее молча, и без этой
      // строки часть карточек просто осталась бы без бейджа без объяснений.
      report(trimmed > 0 ? tRef.current('prices.trimmed', { count: sent.length }) : null, owner);

      const ids = sent.map((s) => s.id);
      setBusyIds((prev) => new Set([...prev, ...ids]));

      const ctrl = new AbortController();
      runRef.current = ctrl;
      const { library, currencies } = stateRef.current;
      const res = await checkPrices(
        sent.map((s) => toFlightInput(s, library)),
        currencies,
        tripId,
        ctrl.signal,
      );

      setBusyIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });

      // Отмену просил не человек, а размонтирование — говорить ему об ошибке
      // не о чем.
      if (ctrl.signal.aborted) return;

      if (!res.ok) {
        report(errorMessage(tRef.current, res.error), owner);
        return;
      }
      setChecks((prev) => ({ ...prev, ...byId(res.value.checks) }));

      // `maxFlights` и остаток дневной квоты — живые настройки админки, а не
      // константы сборки: их меняют на работающем сервере. Долгая сессия иначе
      // держала бы цифру, полученную при входе, и предупреждала бы человека по
      // устаревшему потолку.
      //
      // Этот GET ничего не стоит и, главное, ничего не тратит: минутный лимит и
      // списание суточной квоты живут в обработчике самой проверки, а конфиг —
      // это одно чтение настроек из базы, без сети и провайдеров
      // (server/prices/routes.mjs). Убирать его «ради экономии квоты» не надо.
      void refreshConfig();
      // Заметки запуска — диагностика: почему конкретный рейс не проверен,
      // видно на самой карточке (status/note этой проверки), а общие
      // подробности человеку показывать нечем и незачем.
      if (res.value.notes.length > 0) console.info('/api/prices/check:', res.value.notes);
    },
    [ready, config, tripId, refreshConfig],
  );

  const applyQuote = useCallback(
    async (segment: FlightSegment, quote: PriceQuote, owner?: string): Promise<void> => {
      if (!ready) return;
      const res = await applyPriceQuote(
        {
          segmentId: segment.id,
          fromMinor: segment.price.amount,
          toMinor: quote.normMinor,
          currency: segment.price.currency,
          site: quote.site,
          url: quote.url,
        },
        tripId,
      );
      if (!res.ok) {
        report(errorMessage(tRef.current, res.error), owner);
        return;
      }
      report(null);
      // Ответ — строка kind:'apply'; в «последнюю проверку» она не годится:
      // бейдж рассказывает о том, что нашли, а не о том, что мы сами вписали.
    },
    [ready, tripId, report],
  );

  const checkOf = useCallback((segmentId: string) => checks[segmentId] ?? null, [checks]);
  const busy = useCallback((segmentId: string) => busyIds.has(segmentId), [busyIds]);
  const errorFor = useCallback(
    (owner: string) => (errorOwner !== null && errorOwner === owner ? error : null),
    [error, errorOwner],
  );

  const value = useMemo<PricesApi>(
    () => ({ config, visible, ready, checkOf, busy, refresh, applyQuote, error, errorFor }),
    [config, visible, ready, checkOf, busy, refresh, applyQuote, error, errorFor],
  );

  return (
    <TripIdContext.Provider value={tripId}>
      <PricesContext.Provider value={value}>{children}</PricesContext.Provider>
    </TripIdContext.Provider>
  );
}

export function usePrices(): PricesApi {
  const ctx = useContext(PricesContext);
  if (ctx === undefined) {
    throw new Error('usePrices() должен использоваться внутри <PricesProvider>');
  }
  return ctx;
}
